import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Grid,
  makeStyles,
  Popover,
  TextField,
  Typography
} from '@material-ui/core';
import { FormattedMessage } from 'react-intl';
import { Skeleton } from '@material-ui/lab';
import useKeyBoardEventHandlers from '~/modules/common/hooks/useKeyBoardEventHandlers';
import { NoValue } from '~/modules/common/components';
import NumberInput from '~/modules/common/components/NumberInput';
import { isNullOrUndefined } from '~/modules/common/util';

const resourceLabels = {
  label: <FormattedMessage id="customLineItem.label" />,
  unit: <FormattedMessage id="customLineItem.units" />
};

const useStyles = makeStyles(theme => ({
  skeletonWrapper: {
    width: '100%'
  },
  hover: {
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      cursor: 'pointer'
    }
  }
}));

const usePopoverStyles = makeStyles(theme => ({
  paper: {
    padding: theme.spacing(2),
    width: theme.spacing(64)
  }
}));

const QuantityRenderer = ({
  field,
  record,
  column: {
    isLineItemUpdating,
    editable,
    onCustomLineItemQuantityChange,
    onCustomLineItemUnitChange,
    onCustomLineItemBlur
  }
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const classes = useStyles();
  const popoverClasses = usePopoverStyles();

  const handleOpen = useCallback(
    event => {
      if (editable) setAnchorEl(event.currentTarget);
    },
    [editable]
  );

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const { handleKeyDown } = useKeyBoardEventHandlers({
    onEnterClick: handleOpen
  });

  const hasValue =
    !isNullOrUndefined(record.quantity) || !isNullOrUndefined(record.unit);

  const displayValue = useMemo(() => {
    const { quantity, unit } = record;

    if (!isNullOrUndefined(quantity) && unit) return `${quantity} ${unit}`;

    if (unit) return unit;

    return quantity;
  }, [record]);

  const quantityChangeHandler = useCallback(
    e => onCustomLineItemQuantityChange(e, record),
    [onCustomLineItemQuantityChange, record]
  );

  const unitChangeHandler = useCallback(
    e => onCustomLineItemUnitChange(e, record),
    [onCustomLineItemUnitChange, record]
  );

  const customLineItemBlurHandler = useCallback(
    value => {
      onCustomLineItemBlur(value, record);
      setAnchorEl(null);
    },
    [onCustomLineItemBlur, record]
  );

  if (isLineItemUpdating)
    return (
      <div className={classes.skeletonWrapper}>
        <Skeleton variant="text" width="100%" height={24} />
      </div>
    );

  return (
    <>
      <div
        role="button"
        className={editable ? classes.hover : undefined}
        onClick={handleOpen}
        onKeyDown={handleKeyDown}
        tabIndex={editable ? 0 : -1}
      >
        {hasValue ? (
          <Typography variant="body2">{displayValue}</Typography>
        ) : (
          <NoValue />
        )}
      </div>
      {anchorEl && (
        <Popover
          id="quantity-editor"
          anchorEl={anchorEl}
          open
          onClose={handleClose}
          classes={popoverClasses}
        >
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <NumberInput
                id="lineItems.custom.unit"
                data-qe-id="lineItems.custom.unit"
                variant="filled"
                label={resourceLabels.unit}
                onValueChange={quantityChangeHandler}
                onBlur={customLineItemBlurHandler}
                value={record.quantity}
                align="left"
                fullWidth
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                id="lineItems.custom.label"
                data-qe-id="lineItems.custom.label"
                variant="filled"
                fullWidth
                label={resourceLabels.label}
                onChange={unitChangeHandler}
                onBlur={customLineItemBlurHandler}
                value={record.unit || ''}
              />
            </Grid>
          </Grid>
        </Popover>
      )}
    </>
  );
};

QuantityRenderer.propTypes = {
  column: PropTypes.object,
  field: PropTypes.string.isRequired,
  record: PropTypes.object.isRequired
};

export default QuantityRenderer;
